class CompatibleFirearmsAccordion extends HTMLElement {
  constructor() {
    super();

    this.groups = [];
    this.input = null;
    this.noResults = null;
    this.moreContainer = null;
    this.moreButton = null;
    this.moreToggle = null;
    this.showLabel = null;
    this.hideLabel = null;
    this.clearButton = null;
    this.moreToggleDefaultDisplay = '';

    this.onInput = this.onInput.bind(this);
    this.onMoreToggleClick = this.onMoreToggleClick.bind(this);
    this.onClearClick = this.onClearClick.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    this.initGroups();
    this.bindEvents();
    this.resetToDefaultState();
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
    }

    if (this.moreToggle) {
      this.moreToggle.removeEventListener('click', this.onMoreToggleClick);
    }

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.onClearClick);
    }
  }

  cacheElements() {
    this.input = this.querySelector('[data-compatible-firearms-search]');
    this.noResults = this.querySelector('[data-compatible-firearms-no-results]');
    this.moreContainer = this.querySelector('[data-compatible-firearms-more]');
    this.moreToggle = this.querySelector(
      '[data-compatible-firearms-more-toggle]'
    );
    this.clearButton = this.querySelector(
      '[data-compatible-firearms-clear]'
    );

    this.showLabel =
      this.moreToggle?.querySelector(
        '.compatible-firearms-more-toggle__show'
      ) || null;

    this.hideLabel =
      this.moreToggle?.querySelector(
        '.compatible-firearms-more-toggle__hide'
      ) || null;

    if (this.moreToggle) {
      this.moreToggleDefaultDisplay = this.moreToggle.style.display || '';
    }
  }

  initGroups() {
    const shells = Array.from(
      this.querySelectorAll('[data-compatible-firearms-group-shell]')
    );

    this.groups = shells.map((shell) => {
      const group = shell.querySelector('[data-compatible-firearms-group]');
      const details = shell.querySelector('details');
      const title = shell.querySelector('[data-accordion-title]');
      const items = Array.from(
        shell.querySelectorAll('[data-compatible-firearms-item]')
      );

      const manufacturer =
        title?.dataset.manufacturer?.trim() ||
        group?.dataset.manufacturer?.trim() ||
        '';

      const total =
        parseInt(title?.dataset.total || items.length, 10) || items.length;

      const originalManufacturer =
        title?.dataset.manufacturer?.trim() ||
        title?.textContent.replace(/\s*\(\d+\)\s*$/, '').trim() ||
        group?.dataset.manufacturer?.trim() ||
        '';

      items.forEach((item) => {
        const rawSearch = item.dataset.search || item.textContent || '';
        const normalizedSearch = this.normalize(rawSearch);

        item.dataset.searchNormalized = normalizedSearch;

        const model = item.querySelector('.weapon-model');
        if (model && !model.dataset.originalText) {
          model.dataset.originalText = model.textContent.trim();
        }
      });

      return {
        shell,
        group,
        details,
        title,
        items,
        manufacturer: originalManufacturer || manufacturer,
        total
      };
    });
  }

  bindEvents() {
    if (this.input) {
      this.input.addEventListener('input', this.onInput);
    }

    if (this.moreToggle) {
      this.moreToggle.addEventListener('click', this.onMoreToggleClick);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClearClick);
    }
  }

  onInput() {
    this.filterGroups();
    this.updateClearButtonVisibility();
  }

  onMoreToggleClick() {
    if (!this.moreContainer || !this.moreToggle) return;

    const isExpanded = this.moreToggle.getAttribute('aria-expanded') === 'true';
    const nextExpanded = !isExpanded;

    this.moreContainer.hidden = !nextExpanded;
    this.moreToggle.setAttribute('aria-expanded', String(nextExpanded));

    if (this.showLabel) this.showLabel.hidden = nextExpanded;
    if (this.hideLabel) this.hideLabel.hidden = !nextExpanded;
  }

  onClearClick() {
    if (!this.input) return;

    this.input.value = '';
    this.resetToDefaultState();
    this.updateClearButtonVisibility();
  }

  updateClearButtonVisibility() {
    if (!this.clearButton || !this.input) return;

    const hasValue = this.input.value.trim().length > 0;
    this.clearButton.style.opacity = hasValue ? '1' : '0';
    this.clearButton.style.pointerEvents = hasValue ? 'auto' : 'none';
  }

  hideMoreToggle() {
    if (!this.moreToggle) return;
    this.moreToggle.style.display = 'none';
  }

  showMoreToggle() {
    if (!this.moreToggle) return;
    this.moreToggle.style.display = this.moreToggleDefaultDisplay;
  }

  resetToDefaultState() {
    for (const group of this.groups) {
      const { shell, details, title, items, manufacturer, total } = group;

      shell.classList.remove('is-hidden');

      if (title) {
        title.textContent = manufacturer
          ? `${manufacturer} (${total})`
          : `(${total})`;
      }

      if (details) {
        details.removeAttribute('open');
      }

      for (const item of items) {
        item.classList.remove('is-hidden');
        this.clearHighlights(item);
      }
    }

    if (this.moreContainer) {
      this.moreContainer.hidden = true;
    }

    if (this.moreToggle) {
      this.showMoreToggle();
      this.moreToggle.setAttribute('aria-expanded', 'false');
    }

    if (this.showLabel) {
      this.showLabel.hidden = false;
    }

    if (this.hideLabel) {
      this.hideLabel.hidden = true;
    }

    if (this.noResults) {
      this.noResults.setAttribute('hidden', '');
    }

    this.updateClearButtonVisibility();
  }

  filterGroups() {
    if (!this.input) return;

    const queryRaw = this.input.value.trim();
    const query = this.normalize(queryRaw);

    if (query === '') {
      this.resetToDefaultState();
      return;
    }

    this.hideMoreToggle();

    let totalVisibleItems = 0;

    for (const groupData of this.groups) {
      const { shell, details, title, items, manufacturer } = groupData;

      let visibleCount = 0;

      for (const item of items) {
        const haystack = item.dataset.searchNormalized || '';
        const match = haystack.includes(query);

        item.classList.toggle('is-hidden', !match);

        if (match) {
          visibleCount += 1;
          totalVisibleItems += 1;
          this.updateHighlights(item, queryRaw);
        } else {
          this.clearHighlights(item);
        }
      }

      if (title) {
        title.textContent = manufacturer
          ? `${manufacturer} (${visibleCount})`
          : `(${visibleCount})`;
      }

      if (visibleCount > 0) {
        shell.classList.remove('is-hidden');

        if (details) {
          details.setAttribute('open', 'open');
        }
      } else {
        shell.classList.add('is-hidden');

        if (details) {
          details.removeAttribute('open');
        }
      }
    }

    if (this.moreContainer) {
      this.moreContainer.hidden = false;
    }

    if (this.noResults) {
      if (totalVisibleItems === 0) {
        this.noResults.removeAttribute('hidden');
      } else {
        this.noResults.setAttribute('hidden', '');
      }
    }
  }

  updateHighlights(item, queryRaw) {
    if (!queryRaw) {
      this.clearHighlights(item);
      return;
    }

    const model = item.querySelector('.weapon-model');
    if (!model) return;

    if (!model.dataset.originalText) {
      model.dataset.originalText = model.textContent.trim();
    }

    const originalText = model.dataset.originalText;
    const normalizedText = this.normalize(originalText);
    const normalizedQuery = this.normalize(queryRaw);

    const index = normalizedText.indexOf(normalizedQuery);

    if (index === -1) {
      model.textContent = originalText;
      return;
    }

    const matchLength = queryRaw.length;
    const before = this.escapeHtml(originalText.slice(0, index));
    const match = this.escapeHtml(
      originalText.slice(index, index + matchLength)
    );
    const after = this.escapeHtml(originalText.slice(index + matchLength));

    // Aktivieren wenn das Feature implementiert werden soll
    // model.innerHTML = `${before}<mark>${match}</mark>${after}`;
  }

  clearHighlights(item) {
    const model = item.querySelector('.weapon-model');
    if (!model) return;

    if (model.dataset.originalText) {
      model.textContent = model.dataset.originalText;
    }
  }

  normalize(value) {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  escapeHtml(string) {
    return String(string)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('compatible-firearms-accordion')) {
  customElements.define(
    'compatible-firearms-accordion',
    CompatibleFirearmsAccordion
  );
}